import React from "react";
import { Avatar, Chip, makeStyles } from "@material-ui/core";
import { Add } from "@material-ui/icons";
import FieldArray from "@modules/formx-react/FieldArray";
import { Body } from "@components";
import locale from "@bonliva-crm/locale";
import { WithPopper } from "@bonliva-crm/ui";
import { ThemeType } from "@theme";
import { InvoiceChip } from "./InvoiceChip";
import { InvoicePopup } from "./InvoicePopup";
import { InvoiceNotFound } from "./InvoiceNotFound";

type Props = {
  name: string;
  costCenterId: string;
  canAdd: boolean;
  canView: boolean;
  canDelete: boolean;
  canResync: boolean;
  onAdd: (e: React.SyntheticEvent<any>) => void;
};

export function InvoiceField(props: Props) {
  const classes = useStyles({});

  const invoiceChipStyle: React.CSSProperties = {};

  if (!props.canView) {
    invoiceChipStyle.opacity = 0.5;
    invoiceChipStyle.cursor = "default";
  }

  return (
    <FieldArray<string> name={props.name}>
      {({ remove, value }) => {
        const sorted = value
          .map((x, i) => ({
            invoiceNo: x,
            index: i
          }))
          .sortBy((x) => x.invoiceNo);

        return (
          <div className={classes.root}>
            <Body>{locale.InvoiceNumber}:</Body>

            {sorted.map(({ index, ...params }) => (
              <InvoiceChip
                key={params.invoiceNo}
                invoiceNumber={params.invoiceNo}
                costCenterId={props.costCenterId}
                disabled={!props.canView}
                onDelete={props.canDelete ? () => remove(index) : null}
                style={invoiceChipStyle}
              >
                {({ chip, isError }) => {
                  const popoverContent = isError ? (
                    <InvoiceNotFound />
                  ) : (
                    <InvoicePopup
                      invoiceNumber={params.invoiceNo}
                      costCenterId={props.costCenterId}
                      withDetails={props.canView}
                      withResync={props.canResync}
                    />
                  );

                  if (props.canView) {
                    return (
                      <WithPopper popperContent={popoverContent} placement="bottom">
                        {chip}
                      </WithPopper>
                    );
                  }

                  return chip;
                }}
              </InvoiceChip>
            ))}

            {props.canAdd && (
              <Chip
                avatar={
                  <Avatar>
                    <Add fontSize="small" />
                  </Avatar>
                }
                color="secondary"
                style={{ color: "white" }}
                label={locale.Add}
                onClick={props.onAdd}
              />
            )}
          </div>
        );
      }}
    </FieldArray>
  );
}

const useStyles = makeStyles((theme: ThemeType) => ({
  root: {
    ...theme.mixins.childrenMarginAll("0 4px 4px 0"),
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap"
  }
}));
